/** DP-05 — Empty and error states (07-11) */
import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { Button } from '../components/Button.js';
import { EMPTY_STATE_DP_MAP, type EmptyStateVariant } from './catalog.js';

export interface EmptyStateProps {
  variant: EmptyStateVariant;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  requestId?: string;
  queuedCount?: number;
  /** DP-05f under `floor` additionally reports the oldest queued action's age. */
  oldestQueuedLabel?: string;
  className?: string;
  children?: ReactNode;
}

function livePoliteness(variant: EmptyStateVariant): 'polite' | 'assertive' | 'off' {
  if (variant === 'loadFailed' || variant === 'offline') {
    return 'assertive';
  }
  if (variant === 'empty' || variant === 'noMatch' || variant === 'noPermission') {
    return 'polite';
  }
  return 'off';
}

export function EmptyState({
  variant,
  title,
  description,
  actionLabel,
  onAction,
  requestId,
  queuedCount,
  oldestQueuedLabel,
  className,
  children,
}: EmptyStateProps) {
  const dpId = EMPTY_STATE_DP_MAP[variant];

  return (
    <div
      data-domain-pattern="DP-05"
      data-empty-variant={variant}
      data-empty-dp={dpId}
      role="status"
      aria-live={livePoliteness(variant)}
      className={cn(
        'flex flex-col gap-field rounded-surface border bg-raised p-field',
        variant === 'loadFailed' ? 'border-line-danger' : 'border-line-subtle',
        className,
      )}
    >
      <h3 className="text-h3 font-medium text-default">{title}</h3>
      {description ? <p className="text-body text-muted">{description}</p> : null}
      {requestId ? (
        <p
          className="font-mono text-code text-muted"
          aria-label={`Request identifier ${requestId}`}
        >
          Ref: {requestId}
        </p>
      ) : null}
      {variant === 'offline' && queuedCount !== undefined ? (
        <p className="text-body-sm text-muted">{queuedCount} actions queued</p>
      ) : null}
      {variant === 'offline' && oldestQueuedLabel ? (
        <p className="text-body-sm text-muted [[data-theme=floor]_&]:text-body">
          Oldest queued: {oldestQueuedLabel}
        </p>
      ) : null}
      {actionLabel && onAction ? (
        <div>
          <Button type="button" onClick={onAction}>
            {actionLabel}
          </Button>
        </div>
      ) : null}
      {children}
    </div>
  );
}
